import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Mail, Clock, Menu, ChevronDown, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { contact, navLinks } from "../mock";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2 shrink-0">
    <div className="relative flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-blue-600 text-white">
      <Plus className="w-6 h-6" strokeWidth={3} />
    </div>
    <div className="leading-none">
      <div className="text-xl font-extrabold tracking-tight">
        <span className="text-blue-700">Ship</span><span className="text-teal-600">Meds</span>
      </div>
      <div className="text-[10px] font-semibold tracking-[0.2em] text-slate-500 uppercase">International Services</div>
    </div>
  </Link>
);

const TopBar = () => (
  <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white text-sm">
    <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-y-2">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
        <a href={`tel:${contact.phoneRaw}`} className="flex items-center gap-2 hover:text-teal-100 transition-colors">
          <Phone className="w-4 h-4" /> <span className="hidden sm:inline">Call Us:</span>{contact.phone}
        </a>
        <a href={contact.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-teal-100 transition-colors">
          <MessageCircle className="w-4 h-4" /> <span className="hidden md:inline">Whatsapp:</span>{contact.phone}
        </a>
        <a href={`mailto:${contact.email}`} className="hidden lg:flex items-center gap-2 hover:text-teal-100 transition-colors">
          <Mail className="w-4 h-4" /> {contact.email}
        </a>
      </div>
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4" /> Working Hours: <span className="font-semibold">{contact.hours}</span>
      </div>
    </div>
  </div>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50">
      <TopBar />
      <div className={`bg-white transition-shadow ${scrolled ? "shadow-md" : "shadow-sm"}`}>
        <div className="max-w-7xl mx-auto px-4 h-[70px] flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.label} className="relative group">
                <Link
                  to={link.to}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-[15px] font-medium whitespace-nowrap transition-colors ${isActive(link.to) ? "text-teal-600 bg-teal-50" : "text-slate-700 hover:text-teal-600 hover:bg-teal-50"}`}
                >
                  {link.label}
                  {link.dropdown && <ChevronDown className="w-4 h-4" />}
                </Link>
                {link.dropdown && (
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="w-56 bg-white rounded-xl card-shadow border border-slate-100 py-2">
                      {link.dropdown.map((item) => (
                        <Link key={item.label} to={item.to} className="block px-4 py-2 text-sm text-slate-600 hover:text-teal-600 hover:bg-teal-50 transition-colors">
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>
          <Link
            to="/courier-charges"
            className="hidden lg:inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-4 py-2.5 rounded-lg whitespace-nowrap transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> Get a Quote
          </Link>

          {/* Mobile */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger className="lg:hidden p-2 rounded-md text-slate-700 hover:bg-slate-100">
              <Menu className="w-6 h-6" />
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] overflow-y-auto">
              <div className="mt-6 flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className="px-3 py-3 rounded-md text-base font-medium text-slate-700 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/courier-charges"
                  onClick={() => setOpen(false)}
                  className="mt-3 inline-flex items-center justify-center gap-2 bg-teal-600 text-white font-semibold px-5 py-3 rounded-lg"
                >
                  <MessageCircle className="w-4 h-4" /> Get a Quote
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
